import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { lastValueFrom, Observable } from "rxjs"
import { IUserPermissionsDto } from "../../types/user";
import { inMenCacheHeaderToken } from "../services/interceptors/cache.interceptor";
import { StorageService } from "../services/storageService.service";
import { JANUS_URL } from "./janusUrl.provider";
import getEnvironmentSettings from "../configs/env-conf-loader";
const _envName = getEnvironmentSettings()?.name;

@Injectable()
export class AuthApiService {

  private readonly $http = inject(HttpClient);
  private readonly _storage = inject(StorageService);
  private readonly _janusUrl = inject(JANUS_URL);
  private readonly _logoutWebsocketTerminationCode: number = 4009;
  private readonly _logoutReconnectTimeoutMs = 1000;
  private readonly _logoutReconnectCounterWindowMs = 30000;
  private readonly _logoutMaxReconnectAttempts = 10;
  private readonly _msWebsocketUrl = (_envName == 'Production' || _envName == 'Prepro') ? 'em-api.airtrfx.com' : 'em-api-dev.airtrfx.com';

  public getGrantedPermissions(): Promise<IUserPermissionsDto[]> {
    const url = `${this._janusUrl}/user-service/v2/access-levels/granted-permissions?excludeOrganizations=true`;
    const request = this.$http.get<IUserPermissionsDto[]>(url);
    return lastValueFrom(request);
  }

  public getUserSessionTimeout(): Promise<{ timeoutAfter: number }> {
    const localTimeout: number = this._storage.get('em-qa-session-timeout') || -1;
    if (localTimeout > -1) return Promise.resolve({ timeoutAfter: localTimeout });
    const url = `${this._janusUrl}/organization-service/v1/profile/session-timeout`;
    const request = this.$http.get<{ timeoutAfter: number }>(url, {
      headers: new HttpHeaders().append(inMenCacheHeaderToken, "")
    });
    return lastValueFrom(request);
  }

  private _createLogoutWebsocket(): WebSocket {
    const accessToken = this._storage.get.token();
    return new WebSocket(`wss://${this._msWebsocketUrl}/ws/users/events?access_token=${accessToken}`);
  }

  public observeLogoutMessages<T = any>(): Observable<MessageEvent<T>> {
    return new Observable<MessageEvent<T>>((subscriber) => {

      let reconnectTimeoutRef: ReturnType<typeof setTimeout> | null = null;
      let websocket: WebSocket | null = null;

      const _clearReconnectTimeout = (): void => {
        if (!reconnectTimeoutRef) return;
        clearTimeout(reconnectTimeoutRef);
        reconnectTimeoutRef = null;
      }

      const _cleanUpSocket = () => {
        if (!websocket) return;
        websocket.onmessage = null;
        websocket.onclose = null;
        if (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING) {
          websocket.close(this._logoutWebsocketTerminationCode);
        }
        websocket = null;
      };

      const _connect = (reconnectCounter = 0, lastReconnectAttempt = 0): void => {
        _cleanUpSocket();
        websocket = this._createLogoutWebsocket();
        websocket.onmessage = (message) => subscriber.next(message);
        websocket.onclose = _reconnect(reconnectCounter, lastReconnectAttempt);
      };

      const _reconnect = (reconnectCounter = 0, lastReconnectAttempt = 0) => {
        return (event: CloseEvent) => {
          if (event.code === this._logoutWebsocketTerminationCode || reconnectCounter >= this._logoutMaxReconnectAttempts) return subscriber.complete();
          const now = Date.now();
          reconnectTimeoutRef = setTimeout(
            () => {
              reconnectTimeoutRef = null;
              _connect(now - lastReconnectAttempt < this._logoutReconnectCounterWindowMs ? reconnectCounter + 1 : 0, now + this._logoutReconnectTimeoutMs);
            },
            this._logoutReconnectTimeoutMs
          );
        }
      }

      _connect();

      return () => {
        _clearReconnectTimeout();
        _cleanUpSocket();
      };
    });
  }
}
